import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { BenefitStartDateValues } from 'core/application/adminDashboard/benefitDetails/design/common/models'
import perksBenefitStartDateApi from 'core/application/adminDashboard/benefitDetails/design/perks/perksBenefitDesignStartDate'
import { now } from 'core/application/commons/dateUtils'

import { Alert, Col, DefaultDatePickerV2, Icon, Row, Tooltip } from 'common/components'
import { useCommandRequestV2 } from 'common/hooks'

import { BaseComponentProps } from '../../common/models'

import '../../common/DesignStyles.less'

const PerksBenefitStartDate: React.FC<BaseComponentProps<BenefitStartDateValues>> = (props) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams() as any

  const [displayBenefitStartDateInfo, setDisplayBenefitStartDateInfo] = useState<boolean>(false)

  const [initialValues] = useState<BenefitStartDateValues>({
    benefitStartDate: props.state.benefitStartDate,
    planPeriodId: props.state.planPeriodId,
  })

  const { sendCommand: sendCommandPerkStartDate, isLoading: removeAdminIsLoading } = useCommandRequestV2(
    (request: BenefitStartDateValues) => perksBenefitStartDateApi(request),
  )

  const onSubmit = async (values: BenefitStartDateValues) => {
    const request = {
      ...values,
      planPeriodId: planPeriod,
    }

    const result = await sendCommandPerkStartDate(request)
    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  useEffect(() => {
    props.setIsFormValid(!!props.state.benefitStartDate)
  }, [])

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        props.setIsFormValid(formValidator(translate).isValidSync(values))
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form onSubmit={handleSubmit} autoComplete="off">
            <Row className={'mb-32'}>
              <Col span={12} className={'benefit_design_page_header'}>
                <h5>{translate('admin-dashboard.benefit-details.perks-benefit-start-date.header')}</h5>

                <h6 className={'inline mr-8'}>
                  {translate('admin-dashboard.benefit-details.perks-benefit-start-date.subheader')}
                </h6>
                <Tooltip
                  title={translate('admin-dashboard.benefit-details.perks-benefit-start-date.subheader-info-tooltip')}
                >
                  <Icon type={'far fa-info-circle'}></Icon>
                </Tooltip>
              </Col>
            </Row>

            <Row>
              <Col span={6}>
                <DefaultDatePickerV2
                  className={'benefit-date-picker'}
                  label={''}
                  name="benefitStartDate"
                  id="benefitStartDate"
                  onChange={() => {
                    if (formik.values.benefitStartDate !== null) {
                      setDisplayBenefitStartDateInfo(true)
                    }
                  }}
                  disabledDate={(d) => !d || d.isBefore(now().add(-1, 'days'))}
                />
              </Col>
            </Row>
            {displayBenefitStartDateInfo && (
              <Row>
                <Col span={12}>
                  <Alert
                    showIcon
                    message={'Benefit Start Date Info'}
                    description={
                      'Changing the benefit start date may impact your perk start and end dates. You must reselect your perk start and end dates before you can finalize your benefit design.Navigate to Class Design page, select ‘edit class’ for each class, and adjust the perk start and end dates on the Set Allowances step'
                    }
                    type={'info'}
                  />
                </Col>
              </Row>
            )}
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    benefitStartDate: Yup.date().required(translate('general.required')).nullable(),
  })

export default PerksBenefitStartDate
